import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useCreateGlobalMaterial,
  useCreateGlobalTool,
  useGlobalCatalogSearch,
} from '@/features/catalog/use-global-catalog'
import type { GlobalCatalogKind } from '@/api/types'

interface LinkToGlobalDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** MATERIAL or TOOL — drives which global table is searched. */
  kind: GlobalCatalogKind
  /**
   * When true, render MATERIAL/TOOL tabs in the dialog so the user can flip what they're
   * searching. Used by the inventory page (rows aren't intrinsically a kind). When false
   * (catalog drawer), the dialog is locked to the row's known kind.
   */
  allowKindToggle?: boolean
  /** Pre-fill seed text. The dialog opens with this in the search field. */
  seedName: string
  /** Seed unit (only used by the create-new path for materials). Pass empty string for tools. */
  seedUnit: string
  /** Called when the user picks an existing global row. */
  onPickExisting: (globalId: string, kind: GlobalCatalogKind) => void
  /** Called when the user creates a new global row + immediately uses it. */
  onPickNewlyCreated: (globalId: string, kind: GlobalCatalogKind) => void
  pending?: boolean
}

/**
 * Phase 15 — dialog for binding a per-project catalog row OR an inventory row to a global
 * entry. Search the shared catalog; pick a match; or "Create new global entry from this row"
 * if no match exists. Used by the catalog drawer's "Link to global" action and the
 * /me/inventory page's same action.
 */
export const LinkToGlobalDialog = ({
  open,
  onOpenChange,
  kind,
  allowKindToggle = false,
  seedName,
  seedUnit,
  onPickExisting,
  onPickNewlyCreated,
  pending = false,
}: LinkToGlobalDialogProps) => {
  const [query, setQuery] = useState(seedName)
  const [activeKind, setActiveKind] = useState<GlobalCatalogKind>(kind)
  const search = useGlobalCatalogSearch(activeKind, query, open)
  const createMaterial = useCreateGlobalMaterial()
  const createTool = useCreateGlobalTool()

  useEffect(() => {
    if (open) {
      setQuery(seedName)
      setActiveKind(kind)
    }
  }, [open, seedName, kind])

  const items = search.data?.items ?? []
  const showCreate = open && query.trim().length > 0 && !search.isLoading && items.length === 0

  const handleCreate = async () => {
    try {
      if (activeKind === 'MATERIAL') {
        const created = await createMaterial.mutateAsync({
          name: query.trim(),
          unit: seedUnit.trim() || 'ea',
        })
        onPickNewlyCreated(created.id, 'MATERIAL')
      } else {
        const created = await createTool.mutateAsync({ name: query.trim() })
        onPickNewlyCreated(created.id, 'TOOL')
      }
    } catch (err) {
      // 409 collision: surface the existing id so the caller can bind to it.
      const e = err as { response?: { status?: number; data?: { existingId?: string } } }
      if (e.response?.status === 409 && e.response.data?.existingId) {
        onPickExisting(e.response.data.existingId, activeKind)
        toast.info('That entry already exists in the shared catalog — using it')
      } else {
        toast.error('Failed to create shared catalog entry')
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Link to shared catalog</DialogTitle>
          <DialogDescription>
            Search for an existing entry, or create a new one from this {activeKind.toLowerCase()}.
          </DialogDescription>
        </DialogHeader>
        {allowKindToggle && (
          <div className="flex gap-2">
            <Button
              type="button"
              variant={activeKind === 'MATERIAL' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveKind('MATERIAL')}
            >
              Materials
            </Button>
            <Button
              type="button"
              variant={activeKind === 'TOOL' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveKind('TOOL')}
            >
              Tools
            </Button>
          </div>
        )}
        <Input
          autoFocus
          placeholder={`Search shared ${activeKind.toLowerCase()}s…`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="max-h-64 overflow-y-auto rounded-md border border-border">
          {search.isLoading ? (
            <div className="space-y-2 p-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : items.length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground">No matches in the shared catalog.</p>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((g) => {
                const unit = 'unit' in g ? (g as { unit: string }).unit : null
                return (
                  <li key={g.id}>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-2 p-2 text-left text-sm hover:bg-accent/40"
                      onClick={() => onPickExisting(g.id, activeKind)}
                      disabled={pending}
                    >
                      <span className="truncate">{g.name}</span>
                      {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
        {showCreate && (
          <p className="text-xs text-muted-foreground">
            No match? Create &ldquo;{query.trim()}&rdquo; as a new shared entry.
          </p>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          {showCreate && (
            <Button
              onClick={handleCreate}
              disabled={pending || createMaterial.isPending || createTool.isPending}
            >
              {createMaterial.isPending || createTool.isPending
                ? 'Creating…'
                : 'Create new shared entry'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
